import {BaseElement, BASIS, OSkinableInit} from "/@back@/commons/basis.js";
import {Button, ButtonActions} from "/@back@/commons/widgets/buttons.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {Action} from "/@lib@/commons/actions.js";
import {IReg, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {IView, IViewsContainer, OViewVisitOptions} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {JUser} from "/@lib@/core/user.js";
import {OUsersGridInit, UsersGrid} from "/@back@/core/widgets/usersGrid.js";

/**
 * Dialogue de sélection d'un ou plusieurs users.
 */
export interface UserSelector extends BaseElement, IView {
	initialize(init: OUserSelectorInit): this
}

export interface OUserSelectorInit extends OSkinableInit {
	userGrid?: OUsersGridInit<any>
	buttonLabel?: string
	selectAndCloseOnDblClick?: boolean
	onSelChange?: (sel: JUser[], usSel: UserSelector) => void
	startSel?: JUser[];
}

export class UserSelector extends BaseElement implements IViewsContainer {
	/** Contexte */
	reg: IReg<IUiEnv>;

	/** Eléments UI */
	userGrid: UsersGrid<any>;
	msgArea: HTMLElement;
	selectBtn: ButtonActions<UserSelector>;

	protected _initialize(init: OUserSelectorInit) {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		REG.reg.installSkin("standard-dialog", sr);
		this._initAndInstallSkin(this.localName, init);

		const initTree = BASIS.newInit(Object.assign({preserveSelectionOnFilter: true} as OUsersGridInit<any>, init.userGrid), this.reg);

		if (init.selectAndCloseOnDblClick) initTree.defaultAction = new Action<any>().setExecute(() => {this.doSelectAndClose()});

		this.userGrid = sr.appendChild(new UsersGrid().initialize(initTree));
		const footer = sr.appendChild(<div id="footer"/>);
		this.msgArea = footer.appendChild(<div id="msg"/>);
		this.selectBtn = footer.appendChild(<Button id="select" label={init.buttonLabel || "Sélectionner"} class="default" ui-context="dialog" onclick={this.onSelectBtn}/>) as ButtonActions<UserSelector>;
		footer.appendChild(<Button id="cancel" label="Annuler" ui-context="dialog" onclick={this.onCancelBtn}/>);

		this.userGrid.addEventListener("grid-select", (ev: CustomEvent) => {
			this.refrehUi();
			if (init.onSelChange) init.onSelChange(this.userGrid.getSelectedUsers(), this);
		});

		if (init.startSel) this.userGrid.initializedAsync.then(() => {this.userGrid.selectByJUser(init.startSel, true)});

		this.refrehUi();
	}

	private refrehUi() {
		const selection = this.userGrid.getSelectedUsers();
		this.selectBtn.disabled = !selection || selection.length == 0;
	}

	setMsg(msg: string, allowSelect: boolean) {
		this.msgArea.textContent = msg;
		this.selectBtn.disabled = !allowSelect;
	}

	doSelectAndClose() {
		const r = this.userGrid.getSelectedUsers();
		if (r.length > 0) POPUP.findPopupableParent(this).close(r);
	}

	onCancelBtn(this: Button) {
		POPUP.findPopupableParent(this).close();
	}

	onSelectBtn(this: Button) {
		DOMSH.findHost<UserSelector>(this).doSelectAndClose();
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		return visitor(this.userGrid);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return visitor(this.userGrid);
	}
}

REG.reg.registerSkin('server-users-selector', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		height: 50vh;
		max-height: 30em;
		min-width: 0;
		flex-direction: column;
		width: 20em;
	}

	#msg {
		flex: 1;
		margin: 0 .5em;
	}
`);

customElements.define("server-users-selector", UserSelector);
